
import _ from "lodash";
import { IFilterModel, SearchRequestBaseModel, SortOrderType } from "../models/SearchRequestModel";
import { AppConstants } from "../models/AppConstants";
import { AllTransactionRequestModel, TransactionReportDetails } from "../models/Transactions";
import moment from "moment";
import { httpService } from "../services/HttpService";
import { Result } from "../models/Result";
import { CountryModel } from "../models/CountryModel";
import { SelectItem } from "../models/SelectItem";
import { ClientTokenRequestData } from "../models/MerchantModel";
import { CardStatus, IssuingClientStatus } from "../models/IssuingClientsModel";

class Utils {
    private size: number = 128;
    private countriesCountryModel: CountryModel[] = [];
    private countriesSelectItem: SelectItem[] = [];
    private countryNames: Map<string, string> = new Map<string, string>();
    private euEeaCountriesCountryModel: CountryModel[] = [];
    private euEeaCountriesSelectItem: SelectItem[] = [];

        getClientStatusBadge = (status?: IssuingClientStatus) => {
            switch (status) {
                case IssuingClientStatus.Active:
                    return "badge-success";
    
                case IssuingClientStatus.VerificationRequested:
                    return "badge-warning";
    
                case IssuingClientStatus.Blocked:
                    return "badge-danger";
    
                case IssuingClientStatus.Pending:
                    return "badge-info";
    
                default:
                    return "badge-info";
            }
        };
    
    getCountriesResult = async (): Promise<Result<CountryModel[]>> => {
        try {
            if (!this.countriesCountryModel || this.countriesCountryModel.length == 0) {
                const countriesResult = await httpService.getCountries();

                if (countriesResult.success) {
                    this.countriesCountryModel = countriesResult.data;
                    this.countriesSelectItem = countriesResult.data.map((m) => {
                        this.countryNames.set(m.id, m.name);
                        return { label: m.name, value: m.id } as SelectItem;
                    });
                } else {
                    return {
                        success: false,
                        data: countriesResult.data,
                        message: countriesResult.message,
                    };
                }
            }

            return {
                success: this.countriesCountryModel.length > 0,
                data: this.countriesCountryModel,
                message: "",
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: "Failed to retrieve countries.",
            };
        }
    };
    getEuEeaCountriesResult = async (): Promise<Result<CountryModel[]>> => {
        try {
            if (!this.euEeaCountriesCountryModel || this.euEeaCountriesCountryModel.length == 0) {
                const countriesResult = await httpService.getEuEeaCountries();

                if (countriesResult.success) {
                    this.euEeaCountriesCountryModel = countriesResult.data;
                    this.euEeaCountriesSelectItem = countriesResult.data.map((m) => {
                        return { label: m.name, value: m.id } as SelectItem;
                    });
                } else {
                    return {
                        success: false,
                        data: countriesResult.data,
                        message: countriesResult.message,
                    };
                }
            }

            return {
                success: this.euEeaCountriesCountryModel.length > 0,
                data: this.euEeaCountriesCountryModel,
                message: "",
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: "Failed to retrieve EU/EEA countries.",
            };
        }
    };

    getEuEeaCountries = (): SelectItem<string>[] => {
        return this.euEeaCountriesSelectItem;
    };

    isSoftDeleted(email: string) {
        if(!email) return false;
        // Check if the email contains the '_DELETED_' string
        return email.includes('_DELETED_');
    }
    getStatusBadgeClass = (status?: CardStatus) => {
        switch (status) {
            case CardStatus.Active:
                return "badge-success";

            case CardStatus.ProducedAndInactive:
                return "badge-info";

            case CardStatus.BlockedFraudSuspected:
                return "badge-warning";


            case CardStatus.Lost:
            case CardStatus.Stolen:
                return "badge-danger";

            case CardStatus.Destroyed:
            case CardStatus.Expired:
                return "badge-light";

            default:
                return "badge-info";
        }
    };

getClientStatusBadgeClass = (status?: IssuingClientStatus) => {
    switch (status) {
        // Onboarding / KYC
        case IssuingClientStatus.VerificationRequested:
        case IssuingClientStatus.Pending:
            return "badge-info";

        case IssuingClientStatus.VerificationInProgress:
            return "badge-info";

        case IssuingClientStatus.Verified:
            return "badge-primary";

        case IssuingClientStatus.Rejected:
            return "badge-danger";

        // Active lifecycle
        case IssuingClientStatus.Active:
            return "badge-success";

        case IssuingClientStatus.Suspended:
            return "badge-warning";

        // Risk / Compliance
        case IssuingClientStatus.Blocked:
        case IssuingClientStatus.BlockedFraudSuspected:
            return "badge-danger";

        // End of lifecycle
        case IssuingClientStatus.Closed:
            return "badge-light";

        default:
            return "badge-info";
    }
};
    getCountries = (): SelectItem<string>[] => {
        return this.countriesSelectItem;
    };

    getCountryName = (id: string): string | undefined => {
        return this.countryNames?.get(id);
    };

    getQueryParameters = () => {
        return new URLSearchParams(window.location.hash.slice(window.location.hash.indexOf("?")));
    };

    getQueryParameter = (param: string) => {
        return this.getQueryParameters().get(param);
    };

    convertFileToBase64(file: File) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const fileData = reader.result?.toString();
                // Strip all data URI prefixes (e.g., "data:application/pdf;base64,")
                // This makes the function work with any file type configured in the database
                // and returns pure base64 data as expected by backend APIs
                const base64Data = fileData?.includes(",")
                    ? fileData.substring(fileData.indexOf(",") + 1)
                    : fileData;
                resolve(base64Data);
            };
            reader.onerror = reject;
        });
    }

    fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]);
            };
            reader.onerror = reject;
        });
    };

    appendBase64Data = (data: string, fileName: string = "") => {
        return fileName.includes("pdf") ? `data:application/pdf;base64,${data}` : `data:image/jpeg;base64,${data}`;
    };

    removeBase64Data = (data: string) => {
        const base64HeaderPattern = /^data:(image\/jpeg|image\/png|image\/svg\+xml|application\/pdf);base64,/;
        return data.replace(base64HeaderPattern, "");
    };

    getIdentifierFromPath = (): number => {
        return parseInt(_.last(window.location.pathname.split("/")) ?? "");
    };

    checkCharacterlength = (character: string): boolean => {
        return character.length <= this.size;
    };

    convertDateToUTC = (date: Date): Date => {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    };

    convertDatesToIso<T extends Record<string, any>>(obj: T): T {
        const result: any = { ...obj };

        for (const key in result) {
            const value = result[key];

            if (value && (value instanceof Date)) {
                result[key] = new Date(value).toISOString();
            }

            if (typeof value === 'string') {

                // Check if the string can be parsed as a valid date. It returns NaN for things like "False", "123", "abc", or email addresses
                const parsed = Date.parse(value);

                if (!isNaN(parsed)) {

                    const BOOLEAN_STRING_REGEX = /^(true|false)$/i;
                    const ISO_LIKE_OBJECT_ID_REGEX = /^[a-z0-9]{24,}$/i;

                    // Extra guard: only convert if original value doesn't look like a boolean or ID
                    if (!value.match(BOOLEAN_STRING_REGEX) && !value.match(ISO_LIKE_OBJECT_ID_REGEX)) {
                        result[key] = new Date(parsed).toISOString();
                    }
                }
            }
        }

        return result;
    }

    formatToYYYYMMDD = (isoString: string) => {
        if (!isoString) {
            return '';
        }

        const date = new Date(isoString);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const year = date.getFullYear();

        return `${year}-${month}-${day}`;
    };

    formatToFullDateTime = (isoString: string) => {
        if (!isoString) {
            return '';
        }

        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    formatAsFullDateIfIso = (value: string): string => {
        // Check if it's an ISO date string
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            return this.formatToFullDateTime(value);
        }
        return value;
    };

    formatAsYYYYMMDDIfIso = (value: string): string => {
        // Check if it's an ISO date string
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            return this.formatToYYYYMMDD(value);
        }
        return value;
    };

    makeKeyReadable = (key: string): string => {
        return key
            .replace(/\[(\d+)\]/g, (match, index) => ` #${parseInt(index) + 1}`) // [0] becomes #1
            .replace(/\./g, ' → ') // dots become arrows
            .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase becomes spaced
            .replace(/\b\w/g, l => l.toUpperCase()); // capitalize words
    };

    manageDataTableFilters = (filters: IFilterModel[]) => {
        const filteredColumns = filters.map((obj) => {
            if (AppConstants.timeStampFiltered.includes(obj.columnName)) {
                return {
                    ...obj,
                    value: utils.convertDateToUTC(new Date(obj.value)).toISOString(),
                };
            } else {
                return obj;
            }
        });
        return filteredColumns;
    };

    buildFullName(firstName?: string | null, middleName?: string | null, lastName?: string | null): string {
        const parts = [firstName, middleName, lastName].filter(p => p && p.trim().length > 0);
        return parts.join(" ");
    }

    isValidImage = (image: File) => {
        const fileExtension = image.name.split(".").pop()?.toLowerCase();
        if (fileExtension && (fileExtension === "jpeg" || fileExtension === "jpg" || fileExtension === "png")) {
            return true;
        }
        return false;
    };

    extractFiltersData = async (searchRequest: SearchRequestBaseModel) => {
        const requestData = {} as AllTransactionRequestModel;
        if (searchRequest.filters != null && searchRequest.filters.length > 0) {
            searchRequest.filters.forEach((filter) => {
                if (filter.value != null) {
                    if (AppConstants.timeStampFiltered.includes(filter.columnName)) {
                        const updatedEndDate = moment.utc(filter.value[1]).add(1, "day").toDate();
                        requestData.timestampFrom = moment(filter.value[0]).format("YYYY-MM-DD");
                        requestData.timestampTo = moment(updatedEndDate).format("YYYY-MM-DD");
                    } else {
                        (requestData as any)[filter.columnName] = filter.value as string;
                    }
                }
            });
        }
        return requestData;
    };

    trxDirection = (): SelectItem[] => {
        return [
            { label: "Credit", value: "C" },
            { label: "Debit", value: "D" },
        ];
    };

    extendedDatatableSorting = async (
        filters: SearchRequestBaseModel,
        transactions: TransactionReportDetails[],
    ): Promise<TransactionReportDetails[]> => {
        if (filters.sortings && filters.sortings.length > 0) {
            const sort = filters.sortings[0];
            const sortOrder = sort.sortOrder === SortOrderType.Ascending ? 1 : -1;
            const sortBy = sort.sortBy as keyof TransactionReportDetails;

            return [...transactions].sort((a: TransactionReportDetails, b: TransactionReportDetails) => {
                const aVal = a[sortBy] ?? "";
                const bVal = b[sortBy] ?? "";
                if (aVal < bVal) return -1 * sortOrder;
                if (aVal > bVal) return 1 * sortOrder;
                return 0;
            });
        }
        return transactions;
    };

    formatCurrency = (amount: any, currencyCode: any) => {
        const formattedAmount = parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        return formattedAmount;
    };

    isNullOrEmptyString = (str?: string) => {
        return str === null || str === "" || str === undefined;
    };

    hasImageExtension = (fileName: string) => {
        const regex = /\.(jpg|jpeg|png|gif|bmp)$/i;
        return regex.test(fileName);
    };

    formatAmount(amount: any) {
        return amount
            .replace(/(\.\d*?)\./, "$1")
            .replace(/[^0-9.]/g, "")
            .replace(/(\.\d\d).+$/, "$1");
    }
    downloadMtlsTokenFile = (rowData: ClientTokenRequestData) => {
        const encodedCertificate = encodeURIComponent(`IdentityServer:MTLS:X509Certificate:${rowData.certificate || ""}`);
        const authServerUrl = process.env.REACT_APP_AUTH_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL;
        const curlCommand =
            `curl --location '${authServerUrl}/connect/mtls/token' \\\n` +
            `--header 'Content-Type: application/x-www-form-urlencoded' \\\n` +
            `--data-urlencode 'client_id=${rowData.client_id || ""}' \\\n` +
            `--data-urlencode 'grant_type=${rowData.grant_type || ""}' \\\n` +
            `--data-urlencode 'scope=${rowData.scope}' \\\n` +
            `--data-urlencode '${encodedCertificate}'`;

        const blob = new Blob([curlCommand], { type: "text/plain;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `curl_${rowData.client_id || "Client"}.txt`;
        link.click();
    };
    formatBalance(amount: any, currencyCode: any) {
        const formattedAmount = parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        const currency = formattedAmount.replace(/[\d.,]/g, "").trim();
        const amountWithoutCurrency = formattedAmount.replace(currency, "").trim();
        return `${amountWithoutCurrency} ${currency}`;
    }

    formatMerchnatBoricaConfigrationKey(input: string): string {
        return input.replace(/(\B[A-Z](?=[a-z]))/g, " $1").replace(/URL/g, " URL");
    }

    validateIban = (input: string) => {
        const codeLengths = {
            AD: 24,
            AE: 23,
            AL: 28,
            AT: 20,
            AZ: 28,
            BA: 20,
            BE: 16,
            BG: 22,
            BH: 22,
            BI: 28,
            BR: 29,
            BY: 28,
            CH: 21,
            CR: 22,
            CY: 28,
            CZ: 24,
            DE: 22,
            DK: 18,
            DO: 28,
            EE: 20,
            EG: 29,
            ES: 24,
            LC: 32,
            FI: 18,
            FO: 18,
            FR: 27,
            GB: 22,
            GE: 22,
            GI: 23,
            GL: 18,
            GR: 27,
            GT: 28,
            HR: 21,
            HU: 28,
            IE: 22,
            IL: 23,
            IQ: 23,
            IS: 26,
            IT: 27,
            JO: 30,
            KW: 30,
            KZ: 20,
            LB: 28,
            LI: 21,
            LT: 20,
            LU: 20,
            LV: 21,
            LY: 25,
            MC: 27,
            MD: 24,
            ME: 22,
            MK: 19,
            MR: 27,
            MT: 31,
            MU: 30,
            NL: 18,
            NO: 15,
            PK: 24,
            PL: 28,
            PS: 29,
            PT: 25,
            QA: 29,
            RO: 24,
            RS: 22,
            SA: 24,
            SC: 31,
            SD: 18,
            SE: 24,
            SI: 19,
            SK: 24,
            SM: 27,
            ST: 25,
            SV: 28,
            TL: 23,
            TN: 24,
            TR: 26,
            UA: 29,
            VA: 22,
            VG: 24,
            XK: 20,
        };
        const iban = input.toUpperCase().replace(/[^A-Z0-9]/g, "");
        const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
        if (!code || iban.length !== (codeLengths as any)[code[1]]) {
            return false;
        }
        const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) => {
            return (letter.charCodeAt(0) - 55).toString();
        });
        return this.mod97(digits) === 1;
    };

    mod97 = (digital: number | string) => {
        digital = digital.toString();
        let checksum: number | string = digital.slice(0, 2);
        let fragment = "";
        for (let offset = 2; offset < digital.length; offset += 7) {
            fragment = checksum + digital.substring(offset, offset + 7);
            checksum = parseInt(fragment, 10) % 97;
        }
        return checksum;
    };

    toPascalCase = (input: string) => {
        return input
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    renderAmount = (amount: number | null | undefined) => {
        return amount != null && amount > 0 ? amount : "-";
    };

    cleanPurposeField = (rawPurpose: string): { purpose: string; referenceCode?: string } => {
        if (!rawPurpose) return { purpose: 'Payment' };

        const referenceCodeMatch = rawPurpose.match(/\bc\s+\d+\/\d+\b/);
        const referenceCode = referenceCodeMatch ? referenceCodeMatch[0] : undefined;

        let cleanedPurpose = rawPurpose.replace(/\bc\s+\d+\/\d+\b/g, '').trim();

        if (!cleanedPurpose) {
            cleanedPurpose = 'Payment';
        }

        cleanedPurpose = cleanedPurpose.replace(/\s+/g, ' ').trim();

        console.log('Cleaned purpose:', { original: rawPurpose, cleaned: cleanedPurpose, referenceCode });
        return { purpose: cleanedPurpose, referenceCode };
    };

    downloadCSV = (csvContent: string, filename: string) => {
        try {
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            throw error;
        }
    };

    downloadCSVFromBase64 = (base64Data: string, filename: string) => {
        try {
            const bytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
            const csvContent = new TextDecoder("utf-8").decode(bytes);
            this.downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Error downloading CSV from base64:', error);
            throw error;
        }
    };

    downloadFileFromBase64 = (contentBase64: string, filename: string) => {
        this.downloadCSVFromBase64(contentBase64, filename);
    };

    formatActionText = (recordId: number | undefined, fallbackIndex?: number): string => {
        const resolvedIndex =
            typeof recordId === 'number' && recordId > 0
                ? recordId
                : (fallbackIndex ?? 0) + 1;

        return `Transaction ${resolvedIndex}`;
    };

    formatHash = (hash: string): string => {
        if (hash.length > 12) {
            return `${hash.substring(0, 6)}...${hash.substring(hash.length - 6)}`;
        }
        return hash;
    };

    formatAmountBGN = (amount: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'BGN',
            minimumFractionDigits: 2
        }).format(amount);
    };

    formatDateShort = (date: Date): string => {
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).format(new Date(date));
    };

    formatDuration = (duration: number): string => {
        if (duration < 60) {
            return `${duration}s`;
        } else if (duration < 3600) {
            const minutes = Math.floor(duration / 60);
            const seconds = duration % 60;
            return `${minutes}m ${seconds}s`;
        } else {
            const hours = Math.floor(duration / 3600);
            const minutes = Math.floor((duration % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    };

    isFailedStatus = (status: string): boolean => {
        return !['Uploaded', 'Processed'].includes(status);
    };

    formatDateTimeSofia = (date: Date | string): string => {
        if (!date) return '-';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '-';
        const parts = new Intl.DateTimeFormat('bg-BG', {
            timeZone: 'Europe/Sofia',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).formatToParts(d);
        const p = (type: string) => parts.find(v => v.type === type)?.value ?? '';
        return `${p('day')}.${p('month')}.${p('year')} ${p('hour')}:${p('minute')}:${p('second')}`;
    };

    formatDateTimeLong = (date: Date | string): string => {
        if (!date) return '-';

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }).format(new Date(date));
    };

    getStatusBadgeColor = (status: string): string => {
        switch (status.toLowerCase()) {
            case 'processed':
                return 'success';
            case 'failed':
                return 'danger';
            case 'processing':
                return 'warning';
            case 'uploaded':
                return 'info';
            default:
                return 'secondary';
        }
    };

    parseErrorMessage = (result: any): string | any => {
        if (!result.message) return 'Operation failed';

        try {
            const errorData = JSON.parse(result.message);

            if (errorData.fileId && errorData.responseCode && errorData.fileStatus) {
                return errorData;
            }

            if (errorData.errors?.Error) {
                return errorData.errors.Error.join('. ');
            }

            if (errorData.title) {
                return `${errorData.title}: ${errorData.errors?.Error?.join('. ') || 'Unknown error'}`;
            }

            return result.message;
        } catch {
            return result.message;
        }
    };

    resolveMerchantId = (selectedMerchant: any, isSuperAdmin: boolean): number => {
        if (!isSuperAdmin) return 0;

        if (typeof selectedMerchant === 'number') return selectedMerchant;
        if (selectedMerchant?.value) return selectedMerchant.value;

        throw new Error('No merchant selected for super admin');
    };

    mapRecordsToTransactions = (transactions: any[], recordValidations: any[]): any[] => {
        if (transactions.length === 0 && recordValidations.length > 0) {
            return recordValidations.map((validation) => ({
                id: validation.lineNumber,
                recipientName: `Transaction ${validation.lineNumber}`,
                iban: '-',
                bic: '-',
                amount: 0,
                purpose: `Transaction ${validation.lineNumber}`,
                referenceCode: '',
                status: validation.isValid ? 'Success' : 'Failed',
                error: validation.errors.length > 0 ? validation.errors.join(', ') : undefined,
                isValid: validation.isValid,
                wasValidated: validation.wasValidated,
                lineContent: validation.lineContent || undefined,
                errors: validation.errors
            }));
        }

        return transactions.map((transaction, index) => {
            const validation = recordValidations[index] || {
                isValid: true,
                errors: [],
                wasValidated: true
            };

            return {
                id: transaction.sequenceNo,
                recipientName: transaction.recipientName?.trim() || '-',
                iban: transaction.recipientIban || '-',
                bic: transaction.bic || '-',
                amount: transaction.amount || 0,
                purpose: transaction.purpose?.trim() || '-',
                referenceCode: '',
                status: validation.isValid ? 'Success' : 'Failed',
                error: transaction.error || (validation.errors.length > 0 ? validation.errors.join(', ') : undefined),
                isValid: validation.isValid,
                wasValidated: validation.wasValidated,
                lineContent: validation.lineContent || undefined,
                errors: transaction.error ? [transaction.error] : validation.errors
            };
        });
    };

    shouldHideGlobalSearch = (): boolean => {
        const roles = JSON.parse(localStorage.getItem("roles") ?? "[]");
        // Hide GlobalSearch only when user has exactly one role which is "Mass Payment Executor"
        return roles.length === 1 && roles[0] === "Mass Payment Executor";
    };

    /**
     * Recursively parses JSON strings within an object/array structure
     * and converts them to proper JSON objects for display
     * 
     * @param obj - The object, array, or value to parse
     * @returns The parsed object with all JSON strings converted to objects
     */
    parseJsonStrings = (obj: any): any => {
        if (obj === null || obj === undefined) {
            return obj;
        }

        // If it's a string, try to parse it as JSON
        if (typeof obj === 'string') {
            // Check if it looks like JSON (starts with { or [)
            const trimmed = obj.trim();
            if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 1) {
                try {
                    const parsed = JSON.parse(obj);
                    // Recursively parse the parsed JSON in case it contains more JSON strings
                    return this.parseJsonStrings(parsed);
                } catch (e) {
                    // If parsing fails, return the original string
                    return obj;
                }
            }
            return obj;
        }

        // If it's an array, recursively parse each element
        if (Array.isArray(obj)) {
            return obj.map(item => this.parseJsonStrings(item));
        }

        // If it's an object, recursively parse each property
        if (typeof obj === 'object') {
            const result: any = {};
            for (const [key, value] of Object.entries(obj)) {
                result[key] = this.parseJsonStrings(value);
            }
            return result;
        }

        // For other types (number, boolean, etc.), return as-is
        return obj;
    };

    /**
     * Cleans and formats error messages that may come in JSON array format
     * @param error - The error message (string or already parsed)
     * @returns Formatted error as string or array of strings
    */
    cleanErrorMessage = (error: string | any): string | string[] => {
        if (!error) {
            return "An error occurred";
        }

        // If it's already an array, return it
        if (Array.isArray(error)) {
            return error.filter(e => e && typeof e === "string");
        }

        // If it's not a string, convert it
        if (typeof error !== "string") {
            return String(error);
        }

        // Try to parse JSON strings like ["error"] or ["error1", "error2"]
        const parsedError = this.parseJsonStrings(error);

        // If parsed to an array, return it as-is (keep as array even for single item)
        if (Array.isArray(parsedError)) {
            const filtered = parsedError.filter(e => e && typeof e === "string");
            return filtered;
        }

        // Return as-is if it's a string
        return typeof parsedError === "string" ? parsedError : error;
    };

    /**
     * Copies JSON data to clipboard as a formatted string
     * 
     * @param data - The data to copy to clipboard
     * @param onSuccess - Optional callback function to call on successful copy
     * @param onError - Optional callback function to call on error
     * @returns Promise that resolves when copy operation completes
     */
    copyJsonToClipboard = async (
        data: any,
        onSuccess?: () => void,
        onError?: () => void
    ): Promise<void> => {
        try {
            const jsonString = JSON.stringify(data, null, 2);
            await navigator.clipboard.writeText(jsonString);
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (onError) {
                onError();
            }
        }
    };

    /**
     * Validates whitelist form inputs
     * @param reason - The reason text for whitelisting
     * @param attachment - The file attachment
     * @returns Validation result with errors and validity status
     */
    validateWhitelistForm = (reason: string, attachment: File | null) => {
        const reasonError = !reason.trim() ? "Reason is required." : "";
        const attachmentError = !attachment ? "An attachment is required." : "";

        return {
            reasonError,
            attachmentError,
            isValid: !reasonError && !attachmentError,
        };
    };

    /**
     * Validates file upload for whitelist (PDF, DOC, DOCX, max 10 MB)
     * @param file - The file to validate
     * @returns Validation result with error message if invalid
     */
    validateWhitelistFile = (file: File | null) => {
        if (!file) {
            return { isValid: false, error: "" };
        }

        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

        if (!AppConstants.WhitelistAllowedFileTypes.includes(file.type) &&
            !AppConstants.WhitelistAllowedFileExtensions.includes(fileExtension)) {
            return {
                isValid: false,
                error: "Unsupported file type. Allowed: PDF, DOC, DOCX."
            };
        }

        if (file.size > AppConstants.WhitelistFileSizeLimit) {
            return {
                isValid: false,
                error: "File is too large (max 10 MB)."
            };
        }

        return { isValid: true, error: "" };
    };

    /**
     * Extracts File object from PrimeReact FileUpload event
     * Handles different event structures from PrimeReact FileUpload component
     * @param event - The event from PrimeReact FileUpload
     * @returns The File object or null if not found
     */
    extractFileFromEvent = (event: any): File | null => {
        return event.files?.[0] ||
            event.originalEvent?.target?.files?.[0] ||
            (event.files && event.files.length > 0 ? event.files[0] : null) ||
            event.target?.files?.[0] ||
            null;
    };

    /**
     * Formats JSON data for display in a preview modal
     * Handles objects, strings, and parsing errors gracefully
     * @param jsonData - The data to format (object, string, or any)
     * @returns Formatted JSON string with proper indentation
     */
    formatJsonForDisplay = (jsonData: any): string => {
        try {
            // If jsonData is already an object, stringify it
            if (typeof jsonData === 'object' && jsonData !== null) {
                return JSON.stringify(jsonData, null, 2);
            }
            // If it's a string, try to parse and re-stringify for formatting
            if (typeof jsonData === 'string') {
                const parsed = JSON.parse(jsonData);
                return JSON.stringify(parsed, null, 2);
            }
            return String(jsonData);
        } catch (error) {
            // If parsing fails, return as is
            return String(jsonData);
        }
    };

    /**
     * Checks if a string is a valid JSON string
     * @param str - The string to check
     * @returns True if the string appears to be JSON
     */
    isJsonString = (str: string): boolean => {
        if (!str || typeof str !== 'string') return false;
        const trimmed = str.trim();
        return (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
            (trimmed.startsWith('[') && trimmed.endsWith(']'));
    };

    /**
     * Parses a JSON string with error handling
     * Attempts to extract and parse JSON from a string if needed
     * @param details - The string or data to parse
     * @returns Parsed JSON object or error object if parsing fails
     */
    parseJsonString = (details: string | any): any => {
        try {
            // If it's not a string, return as is
            if (typeof details !== 'string') {
                return details;
            }

            // Check if it's already a JSON string
            if (details.trim().startsWith('{') || details.trim().startsWith('[')) {
                return JSON.parse(details);
            }

            // Try to extract JSON from the string
            const jsonMatch = details.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                return JSON.parse(jsonMatch[0]);
            }

            // If no JSON found, return raw data
            return { raw: details };
        } catch (error) {
            // If parsing fails, return error object
            return { raw: details, error: 'Failed to parse JSON' };
        }
    };
}

var utils = new Utils();

/** Convert a 2-letter ISO country code (e.g. "BG") to a flag emoji (e.g. "🇧🇬") */
export function countryCodeToFlag(code: string): string {
    if (!code || code.length !== 2) return "";
    const codePoints = code
        .toUpperCase()
        .split("")
        .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65);
    return String.fromCodePoint(...codePoints);
}

export { utils };